import React, { useEffect, useState } from "react";
import "../../styles/clients.css";
import { Plus, Edit, Trash2, RefreshCw, X } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { supabase } from "../../api/supabaseClient";

const EMPTY_FORM = {
    partner_id: null,
    partner_name: "",
    company_name: "",
    mobile: "",
    upi_id: "",
    account_no: "",
    bank_name: "",
    ifsc: "",
    branch: "",
};

const BANK_FIELDS = [
    { key: "account_no", label: "A/c No" },
    { key: "bank_name", label: "Bank Name" },
    { key: "ifsc", label: "IFSC" },
    { key: "branch", label: "Branch" },
];

// Display-only: "A/c No : ..., Bank Name : ..., IFSC : ..., Branch : ..."
const formatBank = (p) =>
    BANK_FIELDS.filter(({ key }) => (p[key] || "").trim())
        .map(({ key, label }) => `${label} : ${p[key]}`)
        .join(", ");

const UPI_REGEX = /^[a-zA-Z0-9._-]{2,256}@[a-zA-Z][a-zA-Z0-9]{1,63}$/;

const Partners = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const { data: partners = [], loading, fetchAll: fetchPartners, save: savePartner, remove: deletePartner } = useGeneric({
        tableName: "partners",
        idColumn: "partner_id",
        orderBy: "partner_name",
        removeOnInsert: ["partner_id"],
    });

    useEffect(() => {
        fetchPartners();
    }, [fetchPartners]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const setBankField = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, bank_details: "" }));
    };

    // ── Validation ───────────────────────────────────────────────────
    const validate = () => {
        const errs = {};

        if (!form.partner_name.trim())
            errs.partner_name = "Partner name is required.";
        else if (form.partner_name.length > 50)
            errs.partner_name = "Max 50 characters.";

        if ((form.company_name || "").length > 100)
            errs.company_name = "Max 100 characters.";

        if (!form.mobile.trim())
            errs.mobile = "Mobile number is required.";
        else if (!/^\d{10}$/.test(form.mobile.trim()))
            errs.mobile = "Mobile must be 10 digits.";

        const upi = (form.upi_id || "").trim();
        if (upi && !UPI_REGEX.test(upi))
            errs.upi_id = "Enter a valid UPI ID (e.g. name@okaxis).";

        const anyBank = BANK_FIELDS.some(({ key }) => (form[key] || "").trim());
        if (anyBank) {
            const missing = BANK_FIELDS.filter(({ key }) => !(form[key] || "").trim()).map(({ label }) => label);
            if (missing.length) errs.bank_details = `Please fill: ${missing.join(", ")}.`;
            else if (!/^\d{6,20}$/.test(form.account_no.trim()))
                errs.bank_details = "A/c No must be 6–20 digits.";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Duplicate mobile check against the database ──────────────────
    const mobileExists = async (mobile, excludeId) => {
        let query = supabase
            .from("partners")
            .select("partner_id, partner_name")
            .eq("mobile", mobile)
            .limit(1);
        if (excludeId) query = query.neq("partner_id", excludeId);
        const { data, error } = await query;
        if (error) throw new Error("Mobile check failed: " + error.message);
        return data && data.length > 0 ? data[0] : null;
    };

    // ── Save ─────────────────────────────────────────────────────────
    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const mobile = form.mobile.trim();
            const existing = await mobileExists(mobile, form.partner_id);
            if (existing) {
                setErrors((prev) => ({
                    ...prev,
                    mobile: `Mobile number already exists (Partner: ${existing.partner_name}).`,
                }));
                return;
            }

            const payload = {
                partner_id: form.partner_id,
                partner_name: form.partner_name.trim(),
                company_name: (form.company_name || "").trim() || null,
                mobile,
                upi_id: (form.upi_id || "").trim() || null,
                account_no: (form.account_no || "").trim() || null,
                bank_name: (form.bank_name || "").trim() || null,
                ifsc: (form.ifsc || "").trim().toUpperCase() || null,
                branch: (form.branch || "").trim() || null,
            };
            await savePartner(payload);
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            if (err?.code === "23505") {
                setErrors((prev) => ({ ...prev, mobile: "Mobile number already exists." }));
            } else {
                alert("Save failed: " + (err.message || "Unknown error"));
            }
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setShowForm(true);
    };

    const openEdit = (partner) => {
        setForm({
            ...EMPTY_FORM,
            ...partner,
            company_name: partner.company_name ?? "",
            mobile: partner.mobile ?? "",
            upi_id: partner.upi_id ?? "",
            account_no: partner.account_no ?? "",
            bank_name: partner.bank_name ?? "",
            ifsc: partner.ifsc ?? "",
            branch: partner.branch ?? "",
        });
        setErrors({});
        setShowForm(true);
    };

    const closeModal = () => {
        setShowForm(false);
        setForm(EMPTY_FORM);
        setErrors({});
    };

    // ── Delete ───────────────────────────────────────────────────────
    const handleDelete = (id, e) => {
        e?.stopPropagation();
        if (window.confirm("Are you sure you want to delete this partner?")) {
            deletePartner(id);
        }
    };

    // ── Filter ───────────────────────────────────────────────────────
    const q = search.toLowerCase();
    const filtered = (partners || [])
        .filter(
            (u) =>
                u.partner_name?.toLowerCase().includes(q) ||
                u.company_name?.toLowerCase().includes(q) ||
                u.mobile?.includes(search) ||
                u.upi_id?.toLowerCase().includes(q) ||
                formatBank(u).toLowerCase().includes(q)
        )
        .sort((a, b) => a.partner_name?.localeCompare(b.partner_name));

    return (
        <div className="cl-page">
            {/* ── Panel ── */}
            <div className="cl-panel">
                <div className="cl-panel-heading">
                    <span>Partner Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add Partner">
                        <Plus size={16} />
                    </button>
                </div>

                {/* Search bar */}
                <div className="cl-search-bar">
                    <input
                        type="text"
                        placeholder="Search partners…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="cl-search-input"
                    />
                    <button className="cl-refresh-btn" onClick={fetchPartners} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <div className="cl-table-wrapper">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th>Partner ID</th>
                                    <th>Partner Name</th>
                                    <th>Company Name</th>
                                    <th>Mobile Number</th>
                                    <th>UPI ID</th>
                                    <th>A/c No</th>
                                    <th>Bank Name</th>
                                    <th>IFSC</th>
                                    <th>Branch</th>
                                    <th className="cl-actions-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="cl-empty">
                                            No partners found.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((u, i) => (
                                        <tr
                                            key={u.partner_id ?? i}
                                            className={`${i % 2 === 0 ? "cl-row-even" : "cl-row-odd"} cl-row-clickable`}
                                            onClick={() => openEdit(u)}
                                            title="Click to edit"
                                        >
                                            <td data-label="ID">{u.partner_id}</td>
                                            <td data-label="Partner Name">{u.partner_name}</td>
                                            <td data-label="Company Name">{u.company_name}</td>
                                            <td data-label="Mobile">{u.mobile}</td>
                                            <td data-label="UPI ID">{u.upi_id}</td>
                                            <td data-label="A/c No">{u.account_no}</td>
                                            <td data-label="Bank Name">{u.bank_name}</td>
                                            <td data-label="IFSC">{u.ifsc}</td>
                                            <td data-label="Branch">{u.branch}</td>
                                            <td className="cl-actions-td" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    className="cl-icon-btn cl-edit"
                                                    onClick={() => openEdit(u)}
                                                    title="Edit"
                                                >
                                                    <Edit size={15} />
                                                </button>
                                                <button
                                                    className="cl-icon-btn cl-delete"
                                                    onClick={(e) => handleDelete(u.partner_id, e)}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">
                                {form.partner_id ? "Edit Partner" : "Add Partner"}
                            </h4>
                            <button className="cl-modal-close" onClick={closeModal}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="cl-modal-body">
                            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>

                                {/* Row: Partner Name | Partner ID */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-10">
                                        <label className="cl-control-label">Partner Name</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.partner_name ? " cl-input-error" : ""}`}
                                            placeholder="Partner Name"
                                            maxLength={50}
                                            value={form.partner_name}
                                            onChange={(e) => set("partner_name", e.target.value)}
                                        />
                                        {errors.partner_name && (
                                            <span className="cl-error-msg">{errors.partner_name}</span>
                                        )}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">Partner ID</label>
                                        <input
                                            type="text"
                                            className="cl-form-control cl-readonly"
                                            placeholder="Auto"
                                            value={form.partner_id ?? ""}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                {/* Row: Company Name */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-12">
                                        <label className="cl-control-label">Company Name</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.company_name ? " cl-input-error" : ""}`}
                                            placeholder="Partner Company Name"
                                            maxLength={100}
                                            value={form.company_name}
                                            onChange={(e) => set("company_name", e.target.value)}
                                        />
                                        {errors.company_name && (
                                            <span className="cl-error-msg">{errors.company_name}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Row: Mobile Number | UPI ID */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Mobile Number</label>
                                        <input
                                            type="tel"
                                            className={`cl-form-control${errors.mobile ? " cl-input-error" : ""}`}
                                            placeholder="Mobile Number"
                                            maxLength={10}
                                            value={form.mobile}
                                            onChange={(e) =>
                                                set("mobile", e.target.value.replace(/\D/g, ""))
                                            }
                                        />
                                        {errors.mobile && (
                                            <span className="cl-error-msg">{errors.mobile}</span>
                                        )}
                                    </div>
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">UPI ID</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.upi_id ? " cl-input-error" : ""}`}
                                            placeholder="e.g. name@okaxis"
                                            maxLength={100}
                                            value={form.upi_id}
                                            onChange={(e) => set("upi_id", e.target.value.replace(/\s/g, ""))}
                                        />
                                        {errors.upi_id && (
                                            <span className="cl-error-msg">{errors.upi_id}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Bank Details */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">A/c No</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.bank_details ? " cl-input-error" : ""}`}
                                            placeholder="Account Number"
                                            maxLength={20}
                                            value={form.account_no}
                                            onChange={(e) => setBankField("account_no", e.target.value.replace(/\D/g, ""))}
                                        />
                                    </div>
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Bank Name</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.bank_details ? " cl-input-error" : ""}`}
                                            placeholder="e.g. Federal Bank"
                                            maxLength={60}
                                            value={form.bank_name}
                                            onChange={(e) => setBankField("bank_name", e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">IFSC</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.bank_details ? " cl-input-error" : ""}`}
                                            placeholder="IFSC Code"
                                            maxLength={11}
                                            value={form.ifsc}
                                            onChange={(e) => setBankField("ifsc", e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())}
                                        />
                                    </div>
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Branch</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.bank_details ? " cl-input-error" : ""}`}
                                            placeholder="e.g. Aloor"
                                            maxLength={60}
                                            value={form.branch}
                                            onChange={(e) => setBankField("branch", e.target.value)}
                                        />
                                    </div>
                                </div>
                                {errors.bank_details && (
                                    <div className="cl-form-group">
                                        <span className="cl-error-msg">{errors.bank_details}</span>
                                    </div>
                                )}
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>
                                Close
                            </button>
                            <button
                                className="cl-btn cl-btn-primary"
                                onClick={onSave}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : form.partner_id ? "Update" : "Add"}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default Partners;